import React from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getSFDCData } from "@/entities/Deal/Deal.queries";
import { DetailTableRow } from "../components/DetailTable";
import { getCompany } from "@/entities/Company";
import {
    NAICS_6_DIGIT_CODE,
    NAICS_INDUSTRY_GROUP_OPTIONS,
} from "@/constants/industries.constants";
import { REVENUE_MODEL_V6 } from "@/entities/CapitalApplication/CapitalApplication.constants";
import { useMemoText } from "./useMemoText";
import { MemoTextJsonTypeEnum } from "@/entities/MemoText/MemoText.model";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { useColorizedValue, DataSource } from "./useColorizedValue";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { FinanceLabField, getFinanceLabMetrics } from "@/entities/FinanceLab";
import { getLastValue } from "@/utils/metrics/metricsUtils";

interface UseGeneralDataProps {
    opportunityId?: string;
}

export function useGeneralData({ opportunityId }: UseGeneralDataProps = {}) {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showPreAlphaFields = flags.enabled("show-pre-alpha-fields");
    const useFinanceLabDefault = flags.enabled("use-finance-lab-metrics");
    const sfdcData = useApiQuery(getSFDCData);
    const company = useApiQuery(getCompany);
    const { text } = useMemoText();
    const data = opportunityId
        ? sfdcData.data?.find((data) => data.id === opportunityId)
        : sfdcData.data?.[0];

    const { data: legalNameMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [FinanceLabField.ORGANIZATION_LEGAL_NAME],
        },
    });

    const { data: crmOpportunityMetrics } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [
                FinanceLabField.DEAL_CRM_OPPORTUNITY_ID,
                FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME,
            ],
        },
    });

    const legalNameValues = legalNameMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_LEGAL_NAME,
    )?.values;

    const legalNameValue = getLastValue(legalNameValues);

    const crmOpportunityIdValues = crmOpportunityMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CRM_OPPORTUNITY_ID,
    )?.values;

    const crmOpportunityIdValue = getLastValue(crmOpportunityIdValues);

    const crmOpportunityNameValues = crmOpportunityMetrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME,
    )?.values;

    const crmOpportunityNameValue = getLastValue(crmOpportunityNameValues);

    // Colorized values for general data
    const legalNameColorized = useColorizedValue(
        legalNameValue?.formattedValue || legalNameValue?.value,
        DataSource.FINANCE_LAB,
        FinanceLabField.ORGANIZATION_LEGAL_NAME,
    );
    const industryColorized = useColorizedValue(
        NAICS_INDUSTRY_GROUP_OPTIONS[
            company.data?.capitalApplication.selfReportedIndustry as string
        ],
        DataSource.LEGACY_BACKEND,
        "company.capitalApplication.selfReportedIndustry",
    );
    const revenueModelColorized = useColorizedValue(
        REVENUE_MODEL_V6[
            company.data?.capitalApplication.revenueModel as string
        ],
        DataSource.LEGACY_BACKEND,
        "company.capitalApplication.revenueModel",
    );
    const linkedinColorized = useColorizedValue(
        sfdcData.data?.[0]?.companyPageLinkedinUrl,
        DataSource.SFDC,
        "sfdc.companyPageLinkedinUrl",
    );

    const generalData: DetailTableRow[] = [
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Legal Name",
                      value: legalNameColorized.value,
                      valueClassName: legalNameColorized.className,
                      source: legalNameColorized.source,
                      sourceClassName: legalNameColorized.sourceClassName,
                  },
              ]
            : []),
        {
            label: "Self-Reported Industry",
            value: industryColorized.value,
            valueClassName: industryColorized.className,
            source: industryColorized.source,
            sourceClassName: industryColorized.sourceClassName,
        },
        {
            label: "LinkedIn",
            value:
                linkedinColorized.value !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: linkedinColorized.value,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          `${company.data?.name} - LinkedIn`,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source: linkedinColorized.value ? linkedinColorized.source : null,
            sourceClassName: linkedinColorized.sourceClassName,
        },
        {
            label: "Revenue Model",
            value: revenueModelColorized.value,
            valueClassName: revenueModelColorized.className,
            source: revenueModelColorized.source,
            sourceClassName: revenueModelColorized.sourceClassName,
        },
    ];

    // Colorized values for identifiers data
    const sfdcAccountColorized = useColorizedValue(
        data?.accountName,
        DataSource.SFDC,
        "sfdc.accountName",
    );

    const crmOpportunityId = useFinanceLabDefault
        ? crmOpportunityIdValue?.formattedValue || crmOpportunityIdValue?.value
        : data?.id;
    const sfdcOpportunityIdColorized = useColorizedValue(
        crmOpportunityId,
        useFinanceLabDefault ? DataSource.FINANCE_LAB : DataSource.SFDC,
        useFinanceLabDefault
            ? FinanceLabField.DEAL_CRM_OPPORTUNITY_ID
            : "sfdc.id",
    );

    const crmOpportunityName = useFinanceLabDefault
        ? crmOpportunityNameValue?.formattedValue ||
          crmOpportunityNameValue?.value
        : data?.opportunityName;
    const sfdcOpportunityNameColorized = useColorizedValue(
        crmOpportunityName,
        useFinanceLabDefault ? DataSource.FINANCE_LAB : DataSource.SFDC,
        useFinanceLabDefault
            ? FinanceLabField.DEAL_CRM_OPPORTUNITY_NAME
            : "sfdc.opportunityName",
    );
    const icmIdColorized = useColorizedValue(
        company.data?.id,
        DataSource.LEGACY_BACKEND,
        "company.id",
    );
    const aiNaicsColorized = useColorizedValue(
        NAICS_6_DIGIT_CODE[
            text[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data
                ?.naicsCode as string
        ] || text[MemoTextJsonTypeEnum.COMPANY_METADATA]?.data?.naicsCode,
        DataSource.MEMO_TEXT,
        "memoText.companyMetadata.naicsCode",
    );
    const humNaicsCodeListColorized = useColorizedValue(
        data?.naicsCodeList,
        DataSource.SFDC,
        "sfdc.naicsCodeList",
    );
    const humNaicsIndustryColorized = useColorizedValue(
        data?.naicsIndustry,
        DataSource.SFDC,
        "sfdc.naicsIndustry",
    );

    const identifiersData: DetailTableRow[] = [
        {
            label: "Hum ID",
            value: icmIdColorized.value,
            valueClassName: icmIdColorized.className,
            source: icmIdColorized.source,
            sourceClassName: icmIdColorized.sourceClassName,
        },
        ...(showComingSoon ? [{ label: "DUNS", value: COMING_SOON }] : []),
        {
            label: "Account ID",
            value:
                sfdcAccountColorized.value !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: `https://captec.lightning.force.com/lightning/r/Account/${sfdcAccountColorized.value}/view`,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          sfdcAccountColorized.value,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source: sfdcAccountColorized.value
                ? sfdcAccountColorized.source
                : null,
            sourceClassName: sfdcAccountColorized.sourceClassName,
        },
        {
            label: "Hum NAICS Code",
            value: humNaicsCodeListColorized.value,
            valueClassName: humNaicsCodeListColorized.className,
            source: humNaicsCodeListColorized.source,
            sourceClassName: humNaicsCodeListColorized.sourceClassName,
        },
        {
            label: "Opportunity Name",
            value: sfdcOpportunityNameColorized.value,
            valueClassName: sfdcOpportunityNameColorized.className,
            source: sfdcOpportunityNameColorized.source,
            sourceClassName: sfdcOpportunityNameColorized.sourceClassName,
        },
        {
            label: "Hum NAICS Industry",
            value: humNaicsIndustryColorized.value,
            valueClassName: humNaicsIndustryColorized.className,
            source: humNaicsIndustryColorized.source,
            sourceClassName: humNaicsIndustryColorized.sourceClassName,
        },
        {
            label: "Opportunity ID",
            value:
                sfdcOpportunityIdColorized.value !== "-"
                    ? React.createElement(
                          "a",
                          {
                              href: `https://captec.lightning.force.com/lightning/r/Opportunity/${sfdcOpportunityIdColorized.value}/view`,
                              target: "_blank",
                              rel: "noreferrer",
                              className:
                                  "text-link flex items-center gap-2 group",
                          },
                          sfdcOpportunityIdColorized.value,
                          React.createElement(ArrowTopRightOnSquareIcon, {
                              className:
                                  "h-4 w-4 fill-none stroke-brand-300 group-hover:stroke-brand-400",
                          }),
                      )
                    : "-",
            source: sfdcOpportunityIdColorized.value
                ? sfdcOpportunityIdColorized.source
                : null,
            sourceClassName: sfdcOpportunityIdColorized.sourceClassName,
        },
        {
            label: React.createElement(
                "span",
                { className: "flex items-center gap-2" },
                "NAICS",
                React.createElement(
                    "span",
                    {
                        className:
                            "flex items-center justify-center rounded-full bg-slate-600 px-2 py-1",
                    },
                    React.createElement(SparklesIcon, {
                        className: "h-4 w-4",
                    }),
                ),
            ),
            value: aiNaicsColorized.value,
            valueClassName: aiNaicsColorized.className,
            source: aiNaicsColorized.source,
            sourceClassName: aiNaicsColorized.sourceClassName,
        },
    ];

    return {
        generalData,
        identifiersData,
        isLoading: sfdcData.isLoading,
        error: sfdcData.error,
    };
}
